import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Appointment,
  AppointmentStatus,
  fetchAppointment,
  updateAppointment,
} from '../api/appointments';
import { fetchProductVariants, type ProductVariant } from '../api/productVariants';

const labelCx =
  'text-[11px] font-semibold uppercase tracking-wide text-slate-400';
const inputCx =
  'rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:bg-slate-50';

const ALL_STATUSES: AppointmentStatus[] = [
  'scheduled',
  'confirmed',
  'completed',
  'cancelled',
  'no_show',
];

const AppointmentEditPage: FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [scheduledAt, setScheduledAt] = useState('');
  const [status, setStatus] = useState<AppointmentStatus>('scheduled');
  const [notes, setNotes] = useState('');

  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceOptions, setServiceOptions] = useState<ProductVariant[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);

  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<ProductVariant[]>(
    [],
  );

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!appointmentId) {
      setError('Missing appointment id.');
      setLoading(false);
      return;
    }
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAppointment(appointmentId);
        if (ignore) return;
        setAppointment(data);
        setScheduledAt(data.scheduledAt.slice(0, 16)); // ISO → yyyy-MM-ddTHH:mm
        setStatus(data.status);
        setNotes(data.notes ?? '');
        const existing = data.services ?? [];
        setSelectedServiceIds(existing.map((s) => s.id));
        setSelectedServices(existing);
      } catch (err) {
        if (!ignore) {
          setError(
            err instanceof Error ? err.message : 'Failed to load appointment',
          );
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    void load();
    return () => {
      ignore = true;
    };
  }, [appointmentId]);

  useEffect(() => {
    if (!serviceSearch.trim()) {
      setServiceOptions([]);
      setShowServicesDropdown(false);
      setServicesError(null);
      return;
    }
    let ignore = false;
    const timeoutId = window.setTimeout(async () => {
      setServicesLoading(true);
      setServicesError(null);
      try {
        const resp = await fetchProductVariants({
          page: 1,
          limit: 20,
          search: serviceSearch.trim(),
          isActive: true,
        });
        if (!ignore) {
          setServiceOptions(resp.data);
          setShowServicesDropdown(true);
        }
      } catch (err) {
        if (!ignore) {
          setServicesError(
            err instanceof Error ? err.message : 'Failed to search services.',
          );
          setServiceOptions([]);
        }
      } finally {
        if (!ignore) setServicesLoading(false);
      }
    }, 300);
    return () => {
      ignore = true;
      window.clearTimeout(timeoutId);
    };
  }, [serviceSearch]);

  const toggleService = (variant: ProductVariant) => {
    setSelectedServiceIds((prevIds) =>
      prevIds.includes(variant.id)
        ? prevIds.filter((x) => x !== variant.id)
        : [...prevIds, variant.id],
    );
    setSelectedServices((prev) => {
      const exists = prev.find((v) => v.id === variant.id);
      if (exists) {
        return prev.filter((v) => v.id !== variant.id);
      }
      return [...prev, variant];
    });
  };

  const removeService = (id: string) => {
    setSelectedServiceIds((prevIds) => prevIds.filter((x) => x !== id));
    setSelectedServices((prev) => prev.filter((v) => v.id !== id));
  };

  const handleSave = async () => {
    if (!appointment) return;
    if (!scheduledAt) {
      setSaveError('Appointment date & time is required.');
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      await updateAppointment(appointment.id, {
        scheduledAt: new Date(scheduledAt).toISOString(),
        status,
        notes: notes.trim() || null,
        serviceIds: selectedServiceIds.length ? selectedServiceIds : [],
      });
      navigate('/appointments');
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to update appointment.',
      );
    } finally {
      setSaving(false);
    }
  };

  const customerName = useMemo(() => {
    if (!appointment?.customer) return 'Customer';
    const c = appointment.customer;
    const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
    return (
      name || c.email || c.mobileNumber || c.mobileNumber || 'Unnamed customer'
    );
  }, [appointment]);

  if (loading) {
    return (
      <div className="p-6 text-sm text-slate-600">Loading appointment…</div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="p-6 text-sm text-danger">
        {error || 'Appointment not found.'}
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-10 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <div className="flex flex-col gap-3 pb-0 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-800">
            Edit Appointment
          </h1>
          <p className="max-w-2xl text-sm text-slate-500">
            Update appointment status, date, notes, or services. Customer and
            vehicle cannot be changed.
          </p>
        </div>
      </div>

      <section className="mt-4 space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <h2 className="text-sm font-semibold text-slate-700">
            Customer &amp; vehicle
          </h2>
          <dl className="mt-2 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
            <div>
              <dt className={labelCx}>Customer</dt>
              <dd className="mt-0.5 font-medium text-slate-800">
                {customerName}
              </dd>
            </div>
            <div>
              <dt className={labelCx}>Phone</dt>
              <dd className="mt-0.5">
                {appointment.customer?.mobileNumber ?? '—'}
              </dd>
            </div>
            <div>
              <dt className={labelCx}>Vehicle</dt>
              <dd className="mt-0.5">
                {appointment.vehicle
                  ? [
                      appointment.vehicle.vehicleBrand?.name,
                      appointment.vehicle.vehicleModel?.name,
                      appointment.vehicle.vehicleRegistrationNumber,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className={labelCx}>Location</dt>
              <dd className="mt-0.5">
                {(appointment as any).location?.name ?? '—'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <h2 className="text-sm font-semibold text-slate-700">
            Appointment details
          </h2>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="scheduledAt" className={labelCx}>
                Appointment date &amp; time
              </label>
              <input
                id="scheduledAt"
                type="datetime-local"
                className={inputCx}
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="status" className={labelCx}>
                Status
              </label>
              <select
                id="status"
                className={inputCx}
                value={status}
                onChange={(e) => setStatus(e.target.value as AppointmentStatus)}
              >
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label htmlFor="notes" className={labelCx}>
              Notes
            </label>
            <textarea
              id="notes"
              rows={3}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this appointment…"
            />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-700">
                Services
              </h2>
              <p className="text-xs text-slate-500">
                Add or remove service variants linked to this appointment.
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 md:flex-row">
            <div className="flex-1">
              <label
                htmlFor="serviceSearch"
                className={labelCx}
              >
                Search services
              </label>
              <input
                id="serviceSearch"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Search services by name, code, or ERP ID…"
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
              />
              {servicesError ? (
                <p className="mt-1 text-xs text-danger">{servicesError}</p>
              ) : null}
              <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
                {servicesLoading ? (
                  <span>Searching services…</span>
                ) : serviceSearch && serviceOptions.length > 0 ? (
                  <span>Click to add/remove services.</span>
                ) : (
                  <span>Start typing to search services.</span>
                )}
                {showServicesDropdown && (
                  <button
                    type="button"
                    className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                    onClick={() => {
                      setShowServicesDropdown(false);
                      setServiceOptions([]);
                    }}
                  >
                    Close
                  </button>
                )}
              </div>
              {showServicesDropdown &&
              serviceSearch &&
              serviceOptions.length > 0 ? (
                <div className="mt-2 max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white text-sm shadow-lg">
                  {serviceOptions.map((variant) => {
                    const checked = selectedServiceIds.includes(variant.id);
                    const isService = variant.isCreatedByWorkshopManagement;
                    return (
                      <button
                        key={variant.id}
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-primary/5"
                        onClick={() => toggleService(variant)}
                      >
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-700">
                              {variant.name}
                            </span>
                            <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                              {isService ? 'Service' : 'Product'}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500">
                            {variant.variantCode || variant.erpId}
                          </span>
                        </div>
                        <span
                          className={`inline-flex h-4 w-4 items-center justify-center rounded border text-[10px] ${
                            checked
                              ? 'border-primary bg-primary text-white'
                              : 'border-slate-300 bg-white text-slate-500'
                          }`}
                        >
                          {checked ? '✓' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {selectedServices.length > 0 ? (
              <div className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Added services
                </h3>
                <ul className="mt-2 divide-y divide-slate-100">
                  {selectedServices.map((variant) => (
                    <li
                      key={variant.id}
                      className="flex items-center justify-between py-2 text-sm"
                    >
                      <div>
                        <div className="font-medium text-slate-800">
                          {variant.name}
                        </div>
                        <div className="text-xs text-slate-500">
                          {variant.variantCode || variant.erpId}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-xs font-semibold text-danger hover:underline"
                        onClick={() => removeService(variant.id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>

        {saveError ? (
          <div className="rounded-xl border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
            {saveError}
          </div>
        ) : null}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            onClick={() => navigate('/appointments')}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/60"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default AppointmentEditPage;

